import type { useT } from '@/i18n';

type Translations = ReturnType<typeof useT>;
type Formatter = (value: unknown, t: Translations, raw: string) => string | null;

const EMPTY = '—';

const RULE_KEYS = [
  'slotMinutes',
  'maxReservationsPerSlot',
  'maxCoversPerSlot',
  'pacingMaxCovers',
  'pacingMaxParties',
  'maxConcurrentCovers',
  'maxConcurrentParties',
  'partySizePacing',
  'maxOrdersPerSlot',
  'capacityWindowMinutes',
] as const;

type RuleKey = (typeof RULE_KEYS)[number];

const RULE_MINUTE_KEYS: ReadonlySet<RuleKey> = new Set(['slotMinutes', 'capacityWindowMinutes']);

const SERVICE_LABEL_KEYS = new Map<string, 'openingTime' | 'pickup' | 'delivery' | 'reservation'>([
  ['BASE', 'openingTime'],
  ['PICKUP', 'pickup'],
  ['DELIVERY', 'delivery'],
  ['RESERVATION', 'reservation'],
]);

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isWholeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isWeekday(value: unknown): value is number {
  return isWholeNumber(value) && value <= 6;
}

function isRuleKey(key: string): key is RuleKey {
  return RULE_KEYS.some((ruleKey) => ruleKey === key);
}

function formatShifts(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const parts: string[] = [];
  for (const shift of value) {
    if (!isRecord(shift) || typeof shift.start !== 'string' || typeof shift.end !== 'string') {
      return null;
    }
    parts.push(`${shift.start}–${shift.end}`);
  }
  return parts.length > 0 ? parts.join(', ') : EMPTY;
}

function formatWeekdays(value: unknown, t: Translations): string | null {
  const names = t.orderCapacity;
  if (isWeekday(value)) return names.dayNamesLong[value];
  if (!Array.isArray(value) || !value.every(isWeekday)) return null;
  const days = [...new Set(value)].sort((a, b) => a - b);
  if (days.length === 0 || days.length === 7) return names.everyDay;
  return days.map((day) => names.dayNamesShort[day]).join(', ');
}

function formatServices(value: unknown, t: Translations): string | null {
  if (!Array.isArray(value)) return null;
  const labels: string[] = [];
  for (const service of value) {
    const key = typeof service === 'string' ? SERVICE_LABEL_KEYS.get(service) : undefined;
    if (!key) return null;
    labels.push(t.openingHours[key]);
  }
  if (labels.length === 0) return EMPTY;
  if (new Set(value).size === SERVICE_LABEL_KEYS.size) return t.openingHours.allServices;
  return labels.join(', ');
}

function formatOffset(minutes: number, t: Translations): string {
  const history = t.settingHistory;
  if (minutes > 0 && minutes % 1440 === 0) return history.unitDays(String(minutes / 1440));
  if (minutes > 0 && minutes % 60 === 0) return history.unitHours(String(minutes / 60));
  return history.unitMinutes(String(minutes));
}

function formatOffsets(value: unknown, t: Translations): string | null {
  if (!Array.isArray(value) || !value.every(isWholeNumber)) return null;
  if (value.length === 0) return EMPTY;
  return value.map((minutes) => formatOffset(minutes, t)).join(', ');
}

function formatPhotos(value: unknown, t: Translations, raw: string): string | null {
  if (Array.isArray(value) && value.every((key) => typeof key === 'string')) {
    return value.length > 0 ? t.settingHistory.photoCount(value.length) : EMPTY;
  }
  const entries = raw.match(/"[^"]*"/g);
  if (entries && entries.length > 0) return `${t.settingHistory.photoCount(entries.length)}+`;
  return null;
}

function formatTableAttributes(value: unknown, t: Translations): string | null {
  if (!Array.isArray(value) || !value.every((code) => typeof code === 'string')) return null;
  if (value.length === 0) return EMPTY;
  const labels = new Map(Object.entries(t.settingHistory.tableAttributes));
  return value.map((code) => labels.get(code) ?? code).join(', ');
}

function formatList(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  const items: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string' && typeof item !== 'number') return null;
    items.push(String(item));
  }
  return items.length > 0 ? items.join(', ') : EMPTY;
}

function formatPartySizePacing(value: unknown, t: Translations): string | null {
  if (!Array.isArray(value)) return null;
  const parts: string[] = [];
  for (const rule of value) {
    if (
      !isRecord(rule) ||
      !isWholeNumber(rule.minPartySize) ||
      !isWholeNumber(rule.maxPartiesPerBucket)
    ) {
      return null;
    }
    parts.push(t.settingHistory.partySizePacingRule(rule.minPartySize, rule.maxPartiesPerBucket));
  }
  return parts.join(', ');
}

function formatRule(key: RuleKey, value: unknown, t: Translations): string | null {
  if (key === 'partySizePacing') return formatPartySizePacing(value, t);
  if (value === null) return RULE_MINUTE_KEYS.has(key) ? EMPTY : t.orderCapacity.noCap;
  if (!isWholeNumber(value)) return null;
  return RULE_MINUTE_KEYS.has(key) ? t.settingHistory.unitMinutes(String(value)) : String(value);
}

function formatRules(value: unknown, t: Translations): string | null {
  if (!isRecord(value)) return null;
  if (Object.keys(value).some((key) => key !== 'mode' && !isRuleKey(key))) return null;
  const parts: string[] = [];
  for (const key of RULE_KEYS) {
    if (!(key in value)) continue;
    const rule = value[key];
    if (key === 'partySizePacing' && Array.isArray(rule) && rule.length === 0) continue;
    const text = formatRule(key, rule, t);
    if (text === null) return null;
    parts.push(`${t.settingHistory.ruleFields[key]}: ${text}`);
  }
  return parts.length > 0 ? parts.join(' · ') : EMPTY;
}

const FORMATTERS = new Map<string, Formatter>([
  ['shifts', formatShifts],
  ['dayOfWeek', formatWeekdays],
  ['daysOfWeek', formatWeekdays],
  ['services', formatServices],
  ['reminderOffsetsMinutes', formatOffsets],
  ['rules', formatRules],
  ['galleryKeys', formatPhotos],
  ['attributes', formatTableAttributes],
  ['customTags', formatList],
]);

export function formatStructuredValue(field: string, raw: string, t: Translations): string | null {
  const format = FORMATTERS.get(field);
  return format ? format(parseJson(raw), t, raw) : null;
}

export function visibleChange(
  oldRaw: string | null,
  newRaw: string | null,
  from: string,
  to: string,
): [string, string] {
  return from === to && oldRaw && newRaw && oldRaw !== newRaw ? [oldRaw, newRaw] : [from, to];
}
